import { useEffect, useState } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'

import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { UI_FONT_SIZE } from '@/shared/styles/typography'

interface Props {
  errorMessage: string
  hasPendingBlob: boolean
  onRetry: () => void
  onDownload: () => void
  retrying?: boolean
  /**
   * 再送信では回復しない失敗 (= 409/4xx). 再試行ボタンを隠して DL を主導線にする.
   * エラー文言側も「再試行では解決しない」と案内するため、 ボタンを残すと自己矛盾になる.
   */
  isRetryHopeless?: boolean
}

// 録音停止 (finalize POST) 失敗時の retry/DL UI. Blob を front 側に保持しているため
// 何度でも retry 可能. DL は最終手段 (= 後で手動 upload する保険).
// 服薬指導録音は二度撮り不可能の医療 record 制約のため、 finalize 失敗で audio が
// ロストしないように local Blob 保持 → retry/DL の救済導線をここで出す.
// apps/latest の同名 component と対だが、 物理隔離のため latest を import せず自前で持つ.
//
// オフライン finalize 待ち (= retrying かつ error なし) では、 finalize mutation が復帰時に
// auto-resume するため retry button は不要 (= 押すと再 pause するだけ). DL を逃げ道として
// 強調し、 タブ閉じ前の保存を促す. severity も warning (= 通信待ち) に落とす.
export const RecordingFailedAlert = ({
  errorMessage,
  hasPendingBlob,
  onRetry,
  onDownload,
  retrying,
  isRetryHopeless,
}: Props) => {
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onOnline = () => setIsOffline(false)
    const onOffline = () => setIsOffline(true)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  const offlineWaiting =
    hasPendingBlob && !!retrying && isOffline && !errorMessage

  return (
    <Alert
      severity={offlineWaiting ? 'warning' : 'error'}
      sx={{ alignItems: 'flex-start' }}
    >
      <Stack spacing={1}>
        <Typography variant="body2">
          {offlineWaiting
            ? '通信待ち。 オフラインのため音声を送信できていません。'
            : errorMessage || '停止処理に失敗しました'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colorTokens.text.secondary,
            fontSize: UI_FONT_SIZE.tag,
          }}
        >
          {offlineWaiting
            ? '録音データはこの画面のみに保持されています。 離れる前に [音声をダウンロード] で保存できます。 通信が戻れば自動で送信されます。'
            : hasPendingBlob
            ? '録音データはこの画面のみに保持されています。 ページを離れる前に再試行 or ダウンロードしてください。'
            : '録音データは既にサーバへ送信済です。 詳細ページで状態を確認してください。'}
        </Typography>
        {hasPendingBlob && (
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            {!offlineWaiting && !isRetryHopeless && (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                disabled={retrying}
              >
                {retrying ? '再試行中…' : '再試行'}
              </Button>
            )}
            <Button
              size="small"
              variant={
                offlineWaiting || isRetryHopeless ? 'contained' : 'outlined'
              }
              color={
                offlineWaiting
                  ? 'warning'
                  : isRetryHopeless
                  ? 'error'
                  : 'inherit'
              }
              startIcon={<DownloadIcon />}
              onClick={onDownload}
            >
              音声をダウンロード
            </Button>
          </Box>
        )}
      </Stack>
    </Alert>
  )
}
